import React, { useState } from "react";
import ConfirmModal from "../ConfirmModal";
import { printingApi } from "../../lib/printing/printingApi";
import {
  PRINTER_FORM_DEFAULTS, validatePrinterForm, printerToForm, printerPatch, printerReachability, printerVisibleError, formatWhen,
  operatorMessage, ROLE_LABELS, DOC_TYPES, DOC_TYPE_LABELS, isCopyCountValid,
} from "../../lib/printing/printingModel";
import { SectionCard, Field, CheckField, inputCls, btnPrimary, btnSecondary, btnDanger, btnLink, Modal, Pill, EmptyState, Notice } from "./PrintingUi";

const REACH_PILL = {
  reachable: ["success", "Reachable (agent report)"],
  unreachable: ["danger", "Unreachable (agent report)"],
  unknown: ["neutral", "Not tested yet"],
  disabled: ["neutral", "Disabled"],
};

export function PrinterFormDialog({ open, onClose, printer, profiles, agents, onSaved, notify }) {
  const editing = Boolean(printer);
  const [form, setForm] = useState(() => (printer ? printerToForm(printer) : { ...PRINTER_FORM_DEFAULTS }));
  const [errors, setErrors] = useState({});
  const [advanced, setAdvanced] = useState(false);
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const profileList = (profiles?.profiles || []).filter((p) => p.provider === "escpos_network");
  const activeAgents = (agents || []).filter((a) => !a.revoked_at && (a.platform || "windows") === "android");
  const codePages = profiles?.code_pages || ["cp437"];
  const cutProfiles = profiles?.cut_profiles || [{ id: "gs_v_66_0", description: "GS V 66 0" }];

  const submit = async (e) => {
    e.preventDefault();
    setApiError(null);
    const v = validatePrinterForm(form, { profiles: profileList, agents: activeAgents });
    setErrors(v.errors);
    if (!v.valid) return;
    setBusy(true);
    try {
      if (editing) {
        const patch = printerPatch(printer, v.payload);
        if (Object.keys(patch).length > 0) await printingApi.patchPrinter(printer.id, patch);
        notify("success", `Saved ${v.payload.name}.`);
      } else {
        await printingApi.createPrinter(v.payload);
        notify("success", `Added ${v.payload.name}. Ask the agent to run a reachability test before printing.`);
      }
      onSaved();
      onClose();
    } catch (err) { setApiError(operatorMessage(err)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} wide testId="printer-form-dialog"
      title={editing ? `Edit ${printer.name}` : "Add a network printer"}
      description="The printer and the Android print agent must be on the same local network. The cloud never connects to the printer; the agent on the tablet does.">
      <form onSubmit={submit} noValidate className="space-y-4">
        {apiError && <Notice tone="danger" testId="printer-form-error">{apiError}</Notice>}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field label="Display name" required error={errors.name} hint="What staff will see, e.g. Front counter receipt printer.">
            <input className={inputCls} value={form.name} onChange={set("name")} data-testid="printer-name" maxLength={120} />
          </Field>
          <Field label="Location (optional)" hint="A store or station label.">
            <input className={inputCls} value={form.location_id} onChange={set("location_id")} data-testid="printer-location" />
          </Field>
          <Field label="Printer role" required error={errors.role} hint="Which documents this printer normally handles.">
            <select className={inputCls} value={form.role} onChange={set("role")} data-testid="printer-role">
              <option value="receipt">Customer receipts</option>
              <option value="kitchen">Kitchen tickets</option>
              <option value="packing">Packing slips</option>
              <option value="general">Any document (multi-role)</option>
            </select>
          </Field>
          <Field label="Connection type">
            <input className={inputCls} value="Network ESC/POS (Ethernet, raw TCP)" readOnly data-testid="printer-provider" />
          </Field>
          <Field label="Certified printer profile" required error={errors.profile_id}>
            <select className={inputCls} value={form.profile_id} onChange={set("profile_id")} data-testid="printer-profile">
              {profileList.length === 0 && <option value="adesso_nuprint_330">Adesso NuPrint 330 (80 mm, Ethernet)</option>}
              {profileList.map((p) => <option key={p.id} value={p.id}>{p.display_name || p.id}</option>)}
            </select>
          </Field>
          <Field label="Assigned print agent" error={errors.agent_id} hint="The tablet that will send jobs to this printer.">
            <select className={inputCls} value={form.agent_id} onChange={set("agent_id")} data-testid="printer-agent">
              <option value="">Not assigned yet</option>
              {activeAgents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
          <Field label="Printer IP address or local hostname" required error={errors.host} hint="Private LAN only, e.g. 192.168.x.x or kitchen-printer.local. Public Internet addresses are refused.">
            <input className={inputCls} value={form.host} onChange={set("host")} data-testid="printer-host" inputMode="decimal" autoComplete="off" />
          </Field>
          <Field label="Port" required error={errors.port} hint="Raw ESC/POS printers use 9100.">
            <input className={inputCls} value={form.port} onChange={set("port")} data-testid="printer-port" inputMode="numeric" />
          </Field>
        </div>
        <div className="flex flex-wrap gap-4">
          <CheckField label="Enabled" checked={form.enabled} onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} testId="printer-enabled" />
          <CheckField label="Default printer" hint="Used when a document has no printer or role match." checked={form.is_default} onChange={(v) => setForm((f) => ({ ...f, is_default: v }))} testId="printer-default" />
        </div>
        <div className="rounded-md border border-stone-200">
          <button type="button" className={`${btnLink} w-full justify-between px-3`} aria-expanded={advanced} aria-controls="printer-advanced" onClick={() => setAdvanced((v) => !v)} data-testid="printer-advanced-toggle">
            <span>Advanced transport settings</span><span aria-hidden="true">{advanced ? "▲" : "▼"}</span>
          </button>
          {advanced && (
            <div id="printer-advanced" className="grid grid-cols-1 gap-3 border-t border-stone-200 p-3 md:grid-cols-3" data-testid="printer-advanced">
              <Field label="Connect timeout (ms)" error={errors.connect_timeout_ms}><input className={inputCls} value={form.connect_timeout_ms} onChange={set("connect_timeout_ms")} data-testid="printer-connect-timeout" inputMode="numeric" /></Field>
              <Field label="Write timeout (ms)" error={errors.write_timeout_ms}><input className={inputCls} value={form.write_timeout_ms} onChange={set("write_timeout_ms")} data-testid="printer-write-timeout" inputMode="numeric" /></Field>
              <Field label="Paper width (mm)" error={errors.paper_width_mm}><input className={inputCls} value={form.paper_width_mm} onChange={set("paper_width_mm")} data-testid="printer-paper" inputMode="numeric" /></Field>
              <Field label="Columns" error={errors.columns}><input className={inputCls} value={form.columns} onChange={set("columns")} data-testid="printer-columns" inputMode="numeric" /></Field>
              <Field label="Code page"><select className={inputCls} value={form.code_page} onChange={set("code_page")} data-testid="printer-codepage">{codePages.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
              <Field label="Cutter profile"><select className={inputCls} value={form.cut_profile} onChange={set("cut_profile")} data-testid="printer-cut">{cutProfiles.map((c) => <option key={c.id} value={c.id}>{c.description || c.id}</option>)}</select></Field>
              <div className="md:col-span-3">
                <CheckField label="Cash drawer kick" hint="Off by default. Only enable if the printer profile has a verified drawer sequence." checked={form.cash_drawer_enabled} onChange={(v) => setForm((f) => ({ ...f, cash_drawer_enabled: v }))} testId="printer-drawer" />
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="submit" className={btnPrimary} disabled={busy} data-testid="printer-save">{busy ? "Saving…" : editing ? "Save changes" : "Add printer"}</button>
        </div>
      </form>
    </Modal>
  );
}

export function TestPrintDialog({ open, onClose, printers, printerId, onQueued, notify }) {
  const enabled = printers.filter((p) => p.enabled !== false && !p.deleted_at);
  const [form, setForm] = useState({ printer_id: printerId || (enabled[0]?.id || ""), doc_type: "customer_receipt", copies: "1" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const copies = Number(form.copies);
    if (!isCopyCountValid(copies)) { setError("Copies must be between 1 and 5."); return; }
    if (!form.printer_id) { setError("Choose a printer."); return; }
    setError(null); setBusy(true);
    try {
      const data = await printingApi.testPrint({ printer_id: form.printer_id, doc_type: form.doc_type, copies });
      notify("success", `Test print queued (job ${String(data.job?.id || "").slice(0, 8)}…). The agent will pick it up on its next poll.`);
      onQueued(data.job);
      onClose();
    } catch (err) { setError(operatorMessage(err)); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} testId="test-print-dialog" title="Queue a test print"
      description="Queues one job for the print agent. The printout is labelled TEST — NOT A CUSTOMER ORDER. Nothing prints from this browser.">
      <form onSubmit={submit} noValidate className="space-y-3">
        {error && <Notice tone="danger" testId="test-print-error">{error}</Notice>}
        <Field label="Printer" required>
          <select className={inputCls} value={form.printer_id} onChange={(e) => setForm((f) => ({ ...f, printer_id: e.target.value }))} data-testid="test-print-printer">
            <option value="">Choose a printer</option>
            {enabled.map((p) => <option key={p.id} value={p.id}>{p.name} ({ROLE_LABELS[p.role] || p.role})</option>)}
          </select>
        </Field>
        <Field label="Document type" required>
          <select className={inputCls} value={form.doc_type} onChange={(e) => setForm((f) => ({ ...f, doc_type: e.target.value }))} data-testid="test-print-doctype">
            {DOC_TYPES.map((d) => <option key={d} value={d}>{DOC_TYPE_LABELS[d]}</option>)}
          </select>
        </Field>
        <Field label="Copies (1–5)" required>
          <input className={inputCls} value={form.copies} onChange={(e) => setForm((f) => ({ ...f, copies: e.target.value }))} data-testid="test-print-copies" inputMode="numeric" />
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          <button type="submit" className={btnPrimary} disabled={busy} data-testid="test-print-submit">{busy ? "Queuing…" : "Queue test print"}</button>
        </div>
      </form>
    </Modal>
  );
}

export default function PrintersPanel({ printers, agents, profiles, isAdmin, onChanged, notify, onViewJobs }) {
  const [editing, setEditing] = useState(null);      // null | "new" | printer
  const [testFor, setTestFor] = useState(null);      // printer id | null
  const [confirm, setConfirm] = useState(null);      // { kind: "disable"|"enable"|"remove", printer }
  const [busy, setBusy] = useState(false);
  const agentName = (id) => agents.find((a) => a.id === id)?.name || (id ? "Unknown agent" : "Not assigned");
  const live = printers.filter((p) => !p.deleted_at);

  const runConfirmed = async () => {
    const { kind, printer } = confirm;
    setBusy(true);
    try {
      if (kind === "remove") await printingApi.removePrinter(printer.id);
      else await printingApi.patchPrinter(printer.id, { enabled: kind === "enable" });
      notify("success", kind === "remove" ? `Removed ${printer.name}.` : `${printer.name} ${kind === "enable" ? "enabled" : "disabled"}.`);
      setConfirm(null); onChanged();
    } catch (err) { notify("error", operatorMessage(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="printers-panel">
      <SectionCard title="Network printers" description="Printers the Android agent drives over the local network. Reachability comes from the agent's own test, never from the cloud."
        actions={isAdmin && <button type="button" className={btnPrimary} onClick={() => setEditing("new")} data-testid="add-printer-btn">Add network printer</button>}>
        {live.length === 0 ? (
          <EmptyState testId="printers-empty">No printers yet. Add your first network printer and assign it to a print agent.</EmptyState>
        ) : (
          <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-testid="printers-list">
            {live.map((p) => {
              const reach = printerReachability(p);
              const [tone, label] = REACH_PILL[reach];
              return (
                <li key={p.id} className="rounded-md border border-stone-200 p-3 space-y-2" data-testid={`printer-card-${p.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-stone-900">{p.name} {p.is_default && <Pill tone="info">Default</Pill>}</div>
                      <div className="text-xs text-stone-600">{ROLE_LABELS[p.role] || p.role} · {p.profile_id || "no profile"} · {p.host}:{p.port}</div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Pill tone={p.enabled === false ? "neutral" : "success"} testId={`printer-enabled-${p.id}`}>{p.enabled === false ? "Disabled" : "Enabled"}</Pill>
                      <Pill tone={tone} testId={`printer-reach-${p.id}`}>{label}</Pill>
                    </div>
                  </div>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-stone-700 sm:grid-cols-2">
                    <div><dt className="inline text-stone-500">Agent: </dt><dd className="inline">{agentName(p.agent_id)}</dd></div>
                    <div><dt className="inline text-stone-500">Last reachability test: </dt><dd className="inline" data-testid={`printer-reach-at-${p.id}`}>{formatWhen(p.last_reachability_at)}{p.last_reachability_detail ? ` (${p.last_reachability_detail})` : ""}</dd></div>
                    <div><dt className="inline text-stone-500">Last transmission: </dt><dd className="inline">{formatWhen(p.last_transmitted_at)}</dd></div>
                    {printerVisibleError(p) && <div className="sm:col-span-2"><dt className="inline text-stone-500">Last error: </dt><dd className="inline" data-testid={`printer-error-${p.id}`}>{printerVisibleError(p)}</dd></div>}
                  </dl>
                    <details className="text-xs"><summary className="cursor-pointer text-stone-600 min-h-[28px]">Technical details</summary>
                      <div className="mt-1 grid grid-cols-2 gap-1 text-stone-700">
                        <span>Paper: {p.paper_width_mm} mm</span><span>Columns: {p.columns}</span>
                        <span>Code page: {p.code_page}</span><span>Cutter: {p.cut_profile}</span>
                        <span>Connect timeout: {p.connect_timeout_ms} ms</span><span>Write timeout: {p.write_timeout_ms} ms</span>
                        <span>Cash drawer: {p.cash_drawer_enabled ? "enabled" : "off"}</span><span>Status query: {p.status_query_enabled ? "enabled" : "off"}</span>
                      </div>
                    </details>
                  <div className="flex flex-wrap gap-2">
                    {isAdmin && <button type="button" className={btnSecondary} onClick={() => setEditing(p)} data-testid={`printer-edit-${p.id}`}>Edit / reassign</button>}
                    {isAdmin && p.enabled !== false && <button type="button" className={btnSecondary} onClick={() => setTestFor(p.id)} data-testid={`printer-test-${p.id}`}>Queue test print</button>}
                    <button type="button" className={btnSecondary} onClick={() => onViewJobs(p.id)} data-testid={`printer-jobs-${p.id}`}>View recent jobs</button>
                    {isAdmin && (p.enabled === false
                      ? <button type="button" className={btnSecondary} onClick={() => setConfirm({ kind: "enable", printer: p })} data-testid={`printer-enable-${p.id}`}>Enable</button>
                      : <button type="button" className={btnDanger} onClick={() => setConfirm({ kind: "disable", printer: p })} data-testid={`printer-disable-${p.id}`}>Disable</button>)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      {editing && (
        <PrinterFormDialog key={editing === "new" ? "new" : editing.id} open onClose={() => setEditing(null)}
          printer={editing === "new" ? null : editing} profiles={profiles} agents={agents} onSaved={onChanged} notify={notify} />
      )}
      {testFor && <TestPrintDialog open onClose={() => setTestFor(null)} printers={printers} printerId={testFor} onQueued={() => onChanged()} notify={notify} />}
      <ConfirmModal
        open={Boolean(confirm)}
        title={confirm?.kind === "disable" ? `Disable ${confirm?.printer?.name}?` : confirm?.kind === "enable" ? `Enable ${confirm?.printer?.name}?` : `Remove ${confirm?.printer?.name}?`}
        message={confirm?.kind === "disable"
          ? "Jobs routed to this printer will stop being claimed. Queued jobs stay queued; nothing already sent is affected."
          : confirm?.kind === "enable" ? "The agent will start claiming jobs for this printer again." : "The printer is hidden from routing. Existing job history is kept."}
        confirmLabel={confirm?.kind === "disable" ? "Disable printer" : confirm?.kind === "enable" ? "Enable printer" : "Remove"}
        tone={confirm?.kind === "enable" ? "info" : "danger"}
        busy={busy} onCancel={() => setConfirm(null)} onConfirm={runConfirmed} testIdPrefix="printer-confirm"
      />
    </div>
  );
}
